import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Path, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  dentalAndVisionPlansSchema,
  DentalAndVisionPlansSchema,
} from '@features/onboarding/schema/onboarding';
import { DEFAULT_DENTAL_AND_VISION_PLANS_FORM } from '@features/onboarding/constants';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import { FormStepProps } from '@features/onboarding/interfaces/FormStepProps.interface';
import { OnboardingFormStepLayout } from '@features/onboarding/layouts/OnboardingFormStepLayout';
import { FormCard } from '@features/onboarding/components/shared/form-card/FormCard';
import {
  MedicalPlansControls,
  ViewMode,
} from '@features/onboarding/components/shared/form-plan/MedicalPlansControls';
import { Form } from '@components/ui/form';
import { useCompareStore } from 'src/store/compare';
import { ComparePlansModal } from '@features/onboarding/components/shared/plan-compare/ComparePlan';
import { FormPlanList } from '@features/onboarding/components/shared/form-plan/FormPlanList';
import { CoverageTags } from '@features/onboarding/components/shared/coverage-tags/CoverageTags';
import { useInsuranceStore } from 'src/store/insurance';
import { useApplicationStore } from 'src/store/application';
import { sortPlans } from '@utils/sortPlans';
import { usePlan } from '@features/onboarding/hooks/usePlan';
import { getCategoryText, getCategoryIcon } from './content/content';
import { cubicBezier, motion, useReducedMotion } from 'framer-motion';
import { useGeneralStore } from 'src/store';

const EASE = cubicBezier(0.22, 1, 0.36, 1);

const usePageTransitionVariants = (delay = 0) => {
  const shouldReduceMotion = useReducedMotion();

  return {
    initial: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20,
      filter: shouldReduceMotion ? 'none' : 'blur(4px)',
    },
    animate: {
      opacity: 1,
      y: 0,
      filter: 'none',
      transition: {
        duration: 0.6,
        ease: EASE,
        delay,
      },
    },
    exit: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : -10,
      filter: shouldReduceMotion ? 'none' : 'blur(2px)',
      transition: {
        duration: 0.4,
        ease: EASE,
      },
    },
  };
};

export const DentalAndVisionForm = ({
  onSubmit,
  // onInvalid,
  onChange,
  // externalErrorMessage,
}: FormStepProps) => {
  // Move all hooks to the top level
  const {
    compareViewCategory,
    setCompareViewCategory,
    selectedPlans,
    clearSelectedPlans,
  } = useCompareStore();
  const { calculateCoverageLevel, sanitizeCategory, getPlanPrice } = usePlan();
  const { data, isLastSelectedExtraPlan, activeDentalVisionCategory, setLastSelectedExtraPlan, setActiveDentalVisionCategory } =
    useOnboardingStore();
  const plans = useInsuranceStore((state) => state.plans);
  const { fetchActiveSubscriptionPlans, activeCategories, activeSubscriptionPlans } =
    useApplicationStore();
  const { siteConfig } = useGeneralStore();
  const sitePlans = siteConfig?.plans;
  const activePlanIds = useMemo(
    () => new Set(activeSubscriptionPlans?.map((plan) => plan.planId) || []),
    [activeSubscriptionPlans]
  );
  const availablePlans = useMemo(() => {
    const extraPlans = plans.filter(
      (plan) => plan.category && plan.category !== 'Major Medical'
    );
    if (!sitePlans || sitePlans.length === 0) {
      return extraPlans;
    }
    const allowedIds = new Set(sitePlans);
    return extraPlans.filter(
      (plan) => allowedIds.has(plan.id) || activePlanIds.has(plan.id)
    );
  }, [plans, sitePlans, activePlanIds]);
  const availableCategories = useMemo(
    () =>
      Array.from(
        new Set(
          availablePlans
            .map((plan) => plan.category)
            .filter((category): category is string => !!category)
        )
      ),
    [availablePlans]
  );
  
  // Move page transition variants to top level
  const pageTransitionVariants = usePageTransitionVariants();
  
  const applicants = data?.applicants ?? [];
  const extraDependents = applicants.filter(
    (dep) => dep.applicant !== 'primary'
  );
  const showTags = extraDependents.length > 0;

  const EQUITABLE_PLAN_IMAGE = '/images/equitablelogo.svg';
  const DEFAULT_IMAGE = '/icons/vision-plan.svg';
  
  const form = useForm<DentalAndVisionPlansSchema>({
    resolver: zodResolver(dentalAndVisionPlansSchema),
    defaultValues: {
      ...DEFAULT_DENTAL_AND_VISION_PLANS_FORM,
      ...data,
    },
  });
  const planTagsMap = form.watch('planTags') || {};

  const [viewMode, setViewMode] = React.useState<ViewMode>('list');
  const [sortKeys, setSortKeys] = useState<Record<string, string>>({});
  const [sortDirections, setSortDirections] = useState<
    Record<string, 'asc' | 'desc'>
  >({});
  const [expandedPlanIds, setExpandedPlanIds] = useState<
    Record<string, string[]>
  >({});

  useEffect(() => {
    fetchActiveSubscriptionPlans();
  }, [fetchActiveSubscriptionPlans]);

  const groupedPlans = useMemo(() => {
    const displayPlans = availablePlans.map((plan) => {
      const tagsForPlan = planTagsMap[plan.uid] || [];
      const coverageLevel = calculateCoverageLevel(tagsForPlan);
      const coveragePrice = getPlanPrice(plan, coverageLevel);
      const imageSrc = plan.name?.toLowerCase().includes('equitable')
        ? EQUITABLE_PLAN_IMAGE
        : DEFAULT_IMAGE;
      return {
        _id: plan.id,
        id: plan.uid,
        title: plan.name || plan.marketingName || 'Untitled',
        description: plan.shortDescription || plan.shortDescription || '',
        content: plan.longDescription || plan.shortDescription || '',
        image: imageSrc,
        price: `$${coveragePrice.toFixed(2)}`,
        prices: plan.prices,
        period: 'per month',
        category: plan.category!,
      };
    });

    return displayPlans.reduce(
      (acc, plan) => {
        const category = plan.category;
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push(plan);
        return acc;
      },
      {} as Record<string, typeof displayPlans>
    );
  }, [availablePlans, planTagsMap, calculateCoverageLevel, getPlanPrice]);

  const planCategories = useMemo(() => {
    const dentalExists = availableCategories.includes('Dental');
    if (dentalExists) {
      return ['Dental', ...availableCategories.filter((c) => c !== 'Dental')];
    }
    return availableCategories;
  }, [availableCategories]);

  useEffect(() => {
    if (!planCategories.length) {
      return;
    }
    if (activeDentalVisionCategory >= planCategories.length) {
      setActiveDentalVisionCategory(planCategories.length - 1);
    }
  }, [planCategories, activeDentalVisionCategory, setActiveDentalVisionCategory]);

  useEffect(() => {
    const initialSortKeys: Record<string, string> = {};
    const initialSortDirections: Record<string, 'asc' | 'desc'> = {};
    planCategories.forEach((category) => {
      initialSortKeys[category] = 'name';
      initialSortDirections[category] = 'asc';
    });
    setSortKeys(initialSortKeys);
    setSortDirections(initialSortDirections);
  }, [planCategories]);

  const activeCategory = planCategories[activeDentalVisionCategory];
  const { id: idStep, title, description } = getCategoryText(activeCategory ?? '');
  const categoryText = getCategoryText(activeCategory ?? '');
  const isDentalPlanRejected = !!(
    activeCategory && activeCategories.includes(activeCategory)
  );

  const getFormCardContent = () => {
    if (isDentalPlanRejected) {
      return {
        title: '',
        description:
          `**Important Note:** You're welcome to browse the available Extra Coverage plans, but since you already have an active Extra Coverage plan, you won't be able to purchase another plan at this time. Extra Health plans (ancillary plans) can only be changed or added once per year, starting from the effective date of your current plan(s).\n\nIf you have any questions about your coverage—or want to confirm when you'll be eligible to make a new selection—our customer service team is always happy to help. You can reach us anytime at **${siteConfig?.emails?.primary || ''}**.`,
      };
    }

    return {
      title: categoryText.section?.title ?? categoryText.title,
      description:
        categoryText.section?.description ?? categoryText.description,
    };
  };

  const formCardContent = getFormCardContent();
  const sectionTitle = formCardContent.title;
  const sectionDescription = formCardContent.description;

  const prevPlanTagsMapRef = useRef<string>('');

  useEffect(() => {
    const currentPlanTagsMap = planTagsMap || {};
    const currentPlanTagsMapStr = JSON.stringify(currentPlanTagsMap);
    
    // Skip if planTagsMap hasn't actually changed
    if (currentPlanTagsMapStr === prevPlanTagsMapRef.current) {
      return;
    }

    const storeState = useOnboardingStore.getState();
    const currentStorePlanTags = storeState.data?.planTags || {};
    
    // Check if we need to update store
    let needsUpdate = false;
    const updates: Record<string, any> = {};
    const updatedPlanTags: Record<string, string[]> = { ...currentStorePlanTags };

    Object.keys(currentPlanTagsMap).forEach((planUid) => {
      const tags = currentPlanTagsMap[planUid] || [];
      const storeTags = currentStorePlanTags[planUid] || [];
      
      // Skip if tags haven't changed
      if (JSON.stringify(tags.sort()) === JSON.stringify(storeTags.sort())) {
        return;
      }

      needsUpdate = true;
      updatedPlanTags[planUid] = tags;

      let coverageLevel = 1;
      const hasSpouse = tags.some((tag) => tag.startsWith('spouse'));
      const dependentCount = tags.filter((tag) =>
        tag.startsWith('dependent')
      ).length;

      if (hasSpouse && dependentCount > 0) {
        coverageLevel = 4;
      } else if (hasSpouse) {
        coverageLevel = 2;
      } else if (dependentCount > 0) {
        coverageLevel = 3;
      }

      const selectedPlan = availablePlans.find((p) => p.uid === planUid);
      if (!selectedPlan) return;

      const price =
        selectedPlan.prices?.find((p) => p.coverage === coverageLevel)?.price ??
        0;

      const categoryKey = sanitizeCategory(selectedPlan.category);
      const planKey = `${categoryKey}Plan` as keyof DentalAndVisionPlansSchema;
      const selectedPlanUid = form.getValues(planKey);

      if (selectedPlanUid === planUid) {
        const priceKey = `${categoryKey}PlanPrice`;
        const currentPrice = storeState.data?.[priceKey as keyof typeof storeState.data];
        if (currentPrice !== price) {
          updates[priceKey] = price;
        }
      }
    });

    // Also check for removed plans
    Object.keys(currentStorePlanTags).forEach((planUid) => {
      if (!currentPlanTagsMap[planUid]) {
        needsUpdate = true;
        delete updatedPlanTags[planUid];
      }
    });

    if (needsUpdate) {
      useOnboardingStore.setState((state) => ({
        data: {
          ...state.data,
          planTags: updatedPlanTags,
          ...updates,
        },
      }));
      
      prevPlanTagsMapRef.current = currentPlanTagsMapStr;
    }
  }, [planTagsMap, availablePlans, sanitizeCategory, form]);

  const handleNext = form.handleSubmit(() => {
    if (activeDentalVisionCategory < planCategories.length - 1) {
      setActiveDentalVisionCategory(activeDentalVisionCategory + 1);
    } else {
      if (sanitizedCategory) {
        setLastSelectedExtraPlan(sanitizedCategory);
      }
      onSubmit(form.getValues());
    }
  });

  useEffect(() => {
    if (isLastSelectedExtraPlan) {
      const idx = planCategories.findIndex(
        (c) => sanitizeCategory(c) === isLastSelectedExtraPlan
      );
      if (idx !== -1) setActiveDentalVisionCategory(idx);
      setLastSelectedExtraPlan(null);
    }
  }, [isLastSelectedExtraPlan, planCategories]);

  const handlePrev = () => {
    if (activeDentalVisionCategory > 0) {
      setActiveDentalVisionCategory(activeDentalVisionCategory - 1);
    }
  };

  const handleRemovePlan = (id: string, category: string) => {
    const { removePlan } = useCompareStore.getState();
    removePlan(id);
  };

  const handleCloseCompare = () => {
    setCompareViewCategory(null);
    if (clearSelectedPlans) {
      clearSelectedPlans();
    }
  };

  const handleCompareClick = (category: string) => {
    setCompareViewCategory(category);
  };

  const handleSortChange = useCallback(
    (category: string, key: string, direction: 'asc' | 'desc') => {
      setSortKeys((prev) => ({ ...prev, [category]: key }));
      setSortDirections((prev) => ({ ...prev, [category]: direction }));
    },
    []
  );

  const onSortChangeForActiveCategory = useCallback(
    (key: string, direction: 'asc' | 'desc') => {
      handleSortChange(activeCategory, key, direction);
    },
    [activeCategory, handleSortChange]
  );

  const handleAddPlan = () => {
    setCompareViewCategory(null);
  };

  const toggleExpandAll = (category: string, id?: string) => {
    setExpandedPlanIds((prev) => {
      const currentExpanded = prev[category] || [];

      if (viewMode === 'tab' || viewMode === 'list' || viewMode === 'grid') {
        // Toggle only clicked plan
        const next = currentExpanded.includes(id!)
          ? currentExpanded.filter((pid) => pid !== id)
          : [...currentExpanded, id!];
        return { ...prev, [category]: next };
      }

      // Fallback
      return prev;
    });
  };

  const sanitizedCategory = activeCategory
    ? sanitizeCategory(activeCategory)
    : '';
  const categoryPlans = activeCategory
    ? groupedPlans[activeCategory] || []
    : [];
  const sortKey = activeCategory ? sortKeys[activeCategory] || 'name' : 'name';
  const sortDirection = activeCategory
    ? sortDirections[activeCategory] || 'asc'
    : 'asc';

  const sortedPlans = useMemo(() => {
    return sortPlans(categoryPlans, availablePlans, sortKey, sortDirection);
  }, [sortKey, sortDirection, categoryPlans, availablePlans]);

  const planName = sanitizedCategory ? `${sanitizedCategory}Plan` : '';
  const compareName = sanitizedCategory
    ? `${sanitizedCategory}PlansToCompare`
    : '';

  useEffect(() => {
    if (viewMode === 'grid') {
      const allPlanIds = sortedPlans.map((plan) => plan.id);
      const expandedInCurrentCategory = expandedPlanIds[activeCategory] || [];

      if (
        expandedInCurrentCategory.length > 0 &&
        expandedInCurrentCategory.length !== allPlanIds.length
      ) {
        setExpandedPlanIds((prev) => ({
          ...prev,
          [activeCategory]: allPlanIds,
        }));
      }

      if (expandedInCurrentCategory.length === 0) {
        setExpandedPlanIds((prev) => ({
          ...prev,
          [activeCategory]: [],
        }));
      }
    }
  }, [viewMode, sortedPlans, activeCategory]);

  return (
    <OnboardingFormStepLayout
      isFormValid={form.formState.isValid}
      handleFormSubmit={handleNext}
      idStep={idStep}
      title={title}
      description={description}
      onPrev={activeDentalVisionCategory > 0 ? handlePrev : undefined}
      onChange={form.handleSubmit((data) => {
        return onChange(data);
      })}
      headerAnimationKey={`dental-vision-${activeDentalVisionCategory}-${activeCategory}`}
      contentAnimationKey={`dental-vision-content-${activeDentalVisionCategory}-${activeCategory}`}
    >
      {!activeCategory ? (
        <div className="flex items-center justify-center h-full">
          <p>No dental or vision plans available.</p>
        </div>
      ) : compareViewCategory ? (
        <ComparePlansModal
          plans={
            groupedPlans[compareViewCategory]?.filter((plan) => {
              return selectedPlans.includes(plan.id);
            }) || []
          }
          selectedPlanIdsLocal={sortedPlans.map(e => e.id)}
          onRemovePlan={(id) => handleRemovePlan(id, compareViewCategory)}
          onAddPlan={handleAddPlan}
          onReplacePlan={(oldId, newId) => {
            const { removePlan, togglePlan } = useCompareStore.getState();
            removePlan(oldId);
            togglePlan(newId);
          }}
          onClose={handleCloseCompare}
          allPlans={availablePlans}
        />
      ) : (
        <>
          <Form {...form}>
            <form className="mt-0.5 md:m-0 flex flex-col gap-6">
              <motion.div
                key={activeCategory}
                variants={pageTransitionVariants}
                initial="initial"
                animate="animate"
                exit="exit"
              >
                <FormCard
                  key={activeCategory}
                  index={activeDentalVisionCategory + 2}
                  iconComponent={getCategoryIcon(activeCategory)}
                  title={sectionTitle}
                  description={sectionDescription}
                >
                  <CoverageTags
                    form={form}
                    extraDependents={extraDependents}
                    planType={activeCategory.toLowerCase()}
                    showTags={showTags}
                    categoryPlans={categoryPlans}
                  />
                  <MedicalPlansControls
                    form={form}
                    viewMode={viewMode}
                    setViewMode={setViewMode}
                    onSortChange={onSortChangeForActiveCategory}
                    compareName={compareName}
                    isHidePlansBtn={sortedPlans.length === 1 || sortedPlans.length === 2}
                    plans={categoryPlans}
                    selectedPlansLocal={sortedPlans.length === 2 ? sortedPlans : undefined}
                    onOpenCompare={() => handleCompareClick(activeCategory)}
                  />
                  <FormPlanList
                    plans={sortedPlans}
                    form={form}
                    name={planName as Path<DentalAndVisionPlansSchema>}
                    viewMode={viewMode}
                    expandedPlanIds={expandedPlanIds[activeCategory] || []}
                    planLength={sortedPlans.length}
                    onToggleExpand={(id) => toggleExpandAll(activeCategory, id)}
                    onOpenCompare={() => handleCompareClick(activeCategory)}
                    planType={sanitizedCategory}
                    hideRadio={isDentalPlanRejected}
                  />
                </FormCard>
              </motion.div>
            </form>
          </Form>
        </>
      )}
    </OnboardingFormStepLayout>
  );
};
